import * as React from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  fetchStart,
  fetchSuccess,
  fetchError,
  initializeSelection,
  toggleSelection,
  setSelection,
  clearSelection,
  setColumnFilter,
  toggleDistinctFilter,
  setDistinctValues,
  setSort,
  initializeSort,
  setFocusedIndex,
  reset,
  selectItems,
  selectIsLoading,
  selectError,
  selectSelectedIdsSet,
  selectSelectedValues,
  selectColumnFilters,
  selectAllDistinctValues,
  selectFilteredDistinctValues,
  selectSortKey,
  selectSortType,
  selectFocusedIndex,
  selectInitialSelectedIdsSet,
  SortType,
} from "../../../../store/slices/providerBrowserSlice";

interface UseProviderBrowserReduxProps {
  initialValue: string | string[];
  fetchData: () => Promise<any[]>;
  getData: (item: any) => any[];
  labels: string[];
  distinctFilters: string[];
  filteredColumns: string[];
  initialSort?: [string, "asc" | "desc"];
}

function getInitialValues(initialValue: string | string[]): string[] {
  if (!initialValue) return [];
  if (Array.isArray(initialValue)) return initialValue;
  return [initialValue];
}

export function useProviderBrowserRedux({
  initialValue,
  fetchData,
  getData,
  labels,
  distinctFilters,
  filteredColumns,
  initialSort,
}: UseProviderBrowserReduxProps) {
  const dispatch = useAppDispatch();

  // Selectors
  const items = useAppSelector(selectItems);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const selectedIdsSet = useAppSelector(selectSelectedIdsSet);
  const selectedValues = useAppSelector(selectSelectedValues);
  const columnFilters = useAppSelector(selectColumnFilters);
  const allDistinctValues = useAppSelector(selectAllDistinctValues);
  const filteredDistinctValues = useAppSelector(selectFilteredDistinctValues);
  const sortKey = useAppSelector(selectSortKey);
  const sortType = useAppSelector(selectSortType);
  const focusedIndex = useAppSelector(selectFocusedIndex);
  const initialSelectedIdsSet = useAppSelector(selectInitialSelectedIdsSet);

  // Initialize selection, sort, and reset on unmount
  React.useEffect(() => {
    const initialValues = getInitialValues(initialValue);
    dispatch(initializeSelection(initialValues));

    if (initialSort) {
      dispatch(initializeSort({ key: initialSort[0], type: initialSort[1] as SortType }));
    }

    return () => {
      dispatch(reset());
    };
  }, []);

  // Fetch data on mount
  React.useEffect(() => {
    async function prepare() {
      try {
        dispatch(fetchStart());
        const data = await fetchData();
        dispatch(fetchSuccess(data));
      } catch (e: any) {
        let errorMessage = e?.response?.data?.message;
        if (!errorMessage) errorMessage = e.message;
        dispatch(fetchError(errorMessage));
      }
    }
    prepare();
  }, []);

  // Compute distinct values when items change
  React.useEffect(() => {
    if (items.length === 0) return;

    const allDistinct: Record<string, string[]> = {};
    const filteredDistinct: Record<string, string[]> = {};

    for (const distinctFilter of distinctFilters) {
      allDistinct[distinctFilter] = [];
      filteredDistinct[distinctFilter] = [];

      for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
        const label = labels[labelIndex];
        if (distinctFilter !== label) continue;

        items.forEach((item) => {
          const data = getData(item);
          const columnData = data[labelIndex + 1];
          const columnValue = Array.isArray(columnData) ? columnData[0] : columnData;
          if (!allDistinct[distinctFilter].includes(columnValue)) {
            allDistinct[distinctFilter].push(columnValue);
          }
        });
      }
    }

    dispatch(setDistinctValues({ all: allDistinct, filtered: filteredDistinct }));
  }, [items, distinctFilters, labels, getData]);

  // Compute filtered out items - memoized
  const filteredOutItemsSet = React.useMemo(() => {
    const filteredOut = new Set<string>();

    // Apply distinct filters
    for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
      const label = labels[labelIndex];
      const labelFilters = filteredDistinctValues[label];
      if (!labelFilters || labelFilters.length < 1) continue;

      items.forEach((item) => {
        const data = getData(item);
        const itemValue = data[0];
        if (filteredOut.has(itemValue)) return;

        const columnData = data[labelIndex + 1];
        const columnValue = Array.isArray(columnData) ? columnData[0] : columnData;
        if (!labelFilters.includes(columnValue)) {
          filteredOut.add(itemValue);
        }
      });
    }

    // Apply text filters
    for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
      const label = labels[labelIndex];
      const columnFilter = columnFilters[label];
      if (!columnFilter || columnFilter.length < 1) continue;

      items.forEach((item) => {
        const data = getData(item);
        const itemValue = data[0];
        if (filteredOut.has(itemValue)) return;

        const columnData = data[labelIndex + 1];
        const columnValue = Array.isArray(columnData) ? columnData[0] : columnData;
        if (!String(columnValue).toLowerCase().includes(columnFilter.toLowerCase())) {
          filteredOut.add(itemValue);
        }
      });
    }

    return filteredOut;
  }, [items, columnFilters, filteredDistinctValues, labels, getData]);

  // Sort function
  const sortData = React.useCallback(
    (a: any[], b: any[]) => {
      if (sortType === "none") return 0;

      const labelIndex = labels.indexOf(sortKey);
      const key = labelIndex + 1;
      let aValData = a[key];
      let bValData = b[key];

      let aVal = Array.isArray(aValData) ? aValData[0] : aValData;
      let bVal = Array.isArray(bValData) ? bValData[0] : bValData;

      // Default comparison puts larger values first (descending)
      // Swap for ascending to put smaller values first
      if (sortType === "asc") {
        const temp = bVal;
        bVal = aVal;
        aVal = temp;
      }

      if (aVal > bVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    },
    [sortKey, sortType, labels],
  );

  // Items map for O(1) lookup by item value
  const itemsMap = React.useMemo(() => {
    const map = new Map<string, any[]>();
    items.forEach((item) => {
      const data = getData(item);
      map.set(data[0], data);
    });
    return map;
  }, [items, getData]);

  // Processed item IDs - sorted and filtered, with initial selections pinned at top
  const { processedItemIds, pinnedCount } = React.useMemo(() => {
    const itemDatas = items.map(getData);

    const filtered = itemDatas.filter((data) => {
      const itemValue = data[0];
      if (selectedIdsSet.has(itemValue)) return true;
      return !filteredOutItemsSet.has(itemValue);
    });

    // Split into pinned (initially selected) and unpinned
    const pinned: any[][] = [];
    const unpinned: any[][] = [];
    for (const data of filtered) {
      if (initialSelectedIdsSet.has(data[0])) {
        pinned.push(data);
      } else {
        unpinned.push(data);
      }
    }

    // Sort each group independently
    pinned.sort(sortData);
    unpinned.sort(sortData);

    const result = [...pinned, ...unpinned];
    return {
      processedItemIds: result.map((data) => data[0] as string),
      pinnedCount: pinned.length,
    };
  }, [items, filteredOutItemsSet, selectedIdsSet, initialSelectedIdsSet, sortData, getData]);

  // Refetch function
  const refetch = React.useCallback(async () => {
    try {
      dispatch(fetchStart());
      const data = await fetchData();
      dispatch(fetchSuccess(data));
    } catch (e: any) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      dispatch(fetchError(errorMessage));
    }
  }, [fetchData, dispatch]);

  // Memoized actions - stable references
  const actions = React.useMemo(
    () => ({
      toggleSelection: (value: string) => dispatch(toggleSelection(value)),
      setSelection: (value: string) => dispatch(setSelection(value)),
      clearSelection: () => dispatch(clearSelection()),
      setColumnFilter: (column: string, value: string) => dispatch(setColumnFilter({ column, value })),
      toggleDistinctFilter: (column: string, value: string) => dispatch(toggleDistinctFilter({ column, value })),
      setSort: (label: string) => dispatch(setSort(label)),
      setFocusedIndex: (index: number) => dispatch(setFocusedIndex(index)),
      reset: () => dispatch(reset()),
      refetch,
    }),
    [dispatch, refetch],
  );

  // State object for compatibility with existing component interface
  const state = React.useMemo(
    () => ({
      selectedValues,
      items,
      columnFilters,
      allDistinctValues,
      filteredDistinctValues,
      filteredOutItemValues: Array.from(filteredOutItemsSet),
      sortKey,
      sortType,
      focusedIndex,
      isLoading,
      error,
    }),
    [
      selectedValues,
      items,
      columnFilters,
      allDistinctValues,
      filteredDistinctValues,
      filteredOutItemsSet,
      sortKey,
      sortType,
      focusedIndex,
      isLoading,
      error,
    ],
  );

  return {
    state,
    processedItemIds,
    pinnedCount,
    itemsMap,
    selectedIdsSet,
    actions,
  };
}
